/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "Try the new look" strip at the top of the classic UI.
 *
 * Shown after sign-in to anyone still on the classic look who has not picked
 * one (rules in ./offer.js). "Try it" stores v2 as the user's choice on the
 * server, so it sticks across sign-ins and devices until they pick "Switch
 * back to classic" from the profile menu. The close button hides it for this
 * sign-in only.
 */

import { useState } from "react";
import { Sparkles, X } from "lucide-react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useAuth } from "auth/Auth";
import useUiVersion from "hooks/useUiVersion";
import { UI_V2 } from "hooks/uiVersion";
import {
  readDismissedFor,
  sessionIdFromToken,
  shouldOfferNewLook,
  writeDismissedFor,
} from "./offer";

// Brand blue on a light tint, navy ink: every pair passes WCAG AA.
const BLUE = "#1565C8";
const INK = "#0A1B3D";
const TINT = "#EAF2FC";

export default function TryNewLookBanner() {
  const { token } = useAuth();
  const { enabled, ready, isV2, userChoice, locked, setUiVersion } = useUiVersion();
  const sessionId = sessionIdFromToken(token);
  const [dismissedFor, setDismissedFor] = useState(() => readDismissedFor());
  const [switching, setSwitching] = useState(false);

  if (!shouldOfferNewLook({ enabled, ready, isV2, userChoice, locked, sessionId, dismissedFor })) {
    return null;
  }

  const dismiss = () => {
    writeDismissedFor(sessionId);
    setDismissedFor(sessionId);
  };

  const tryIt = async () => {
    setSwitching(true);
    try {
      await setUiVersion(UI_V2); // reloads into the new look
    } catch (error) {
      console.error("Could not switch to the new look:", error);
      setSwitching(false);
    }
  };

  return (
    <Box
      role="region"
      aria-label="Try the new look"
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        px: 2,
        py: 1.25,
        mb: 2,
        borderRadius: "12px",
        border: `1px solid ${BLUE}`,
        backgroundColor: TINT,
        color: INK,
      }}
    >
      <Sparkles size={18} color={BLUE} aria-hidden="true" />
      <Typography component="p" sx={{ flex: 1, minWidth: 220, fontSize: 14, color: INK, m: 0 }}>
        <strong>PearlCRM has a new look.</strong> Same data, cleaner pages and a quicker way to find
        things. You can switch back to classic any time from your profile menu.
      </Typography>
      <Button
        variant="contained"
        disableElevation
        onClick={tryIt}
        disabled={switching}
        sx={{
          backgroundColor: BLUE,
          color: "#FFFFFF !important",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { backgroundColor: "#0F2E66" },
        }}
      >
        {switching ? "Switching…" : "Try the new look"}
      </Button>
      <IconButton size="small" aria-label="Not now" onClick={dismiss} sx={{ color: INK }}>
        <X size={16} />
      </IconButton>
    </Box>
  );
}
